/**
 * BIP-143 SegWit signature hash calculation.
 *
 * Implements the segwit v0 sighash algorithm as specified in BIP-143.
 * Used for signing P2WPKH and P2WSH transaction inputs.
 *
 * @see https://github.com/bitcoin/bips/blob/master/bip-0143.mediawiki
 * @module
 */

import { Buff } from "@vbyte/buff";
import { Assert } from "@vbyte/util";
import { hash160, hash256 } from "@vbyte/crypto/hash";
import * as CONST from "@/const.js";
import { ValidationError } from "@/error.js";

import { decode_script, prefix_script_size } from "@/lib/script/index.js";

import {
	encode_tx_locktime,
	encode_tx_version,
	encode_txin_sequence,
	encode_txin_txid,
	encode_txin_vout,
	encode_vout_value,
	parse_tx,
} from "@/lib/tx/index.js";

import type {
	SigHashOptions,
	TxData,
	TxInput,
	TxOutput,
} from "@/types/index.js";
import { parse_txinput } from "./util.js";

/**
 * Compute the segwit signature hash for a transaction input.
 *
 * This function computes the BIP-143 signature hash used for signing
 * SegWit v0 inputs (P2WPKH and P2WSH). For P2WPKH, provide a pubkey.
 * For P2WSH, provide the witness script.
 *
 * @param txdata - Transaction data object
 * @param options - Sighash options including txindex, sigflag, pubkey or script
 * @returns 32-byte signature hash as Buff
 * @throws {Error} If sigflag is invalid
 * @throws {Error} If prevout value is missing
 * @throws {Error} If neither pubkey nor script is provided
 * @throws {Error} If script contains OP_CODESEPARATOR (not supported)
 *
 * @example
 * ```typescript
 * // P2WPKH signing
 * const hash = hash_segwit_tx(tx, {
 *   txindex: 0,
 *   pubkey: '02...',
 *   sigflag: 0x01 // SIGHASH_ALL
 * })
 *
 * // P2WSH signing
 * const hash = hash_segwit_tx(tx, {
 *   txindex: 0,
 *   script: witnessScript,
 *   sigflag: 0x01
 * })
 * ```
 */
export function hash_segwit_tx(
	txdata: TxData,
	options: SigHashOptions = {},
): Buff {
	// Unpack the sigflag from our config object.
	const { sigflag = 0x01, txindex } = options;
	// Normalize the tx into JSON format.
	const tx = parse_tx(txdata);
	// Check if the ANYONECANPAY flag is set.
	const is_anypay = (sigflag & 0x80) === 0x80;
	// Save a normalized version of the sigflag.
	const flag = sigflag % 0x80;
	// Check if the sigflag exists as a valid type.
	if (!CONST.SIGHASH_SEGWIT.includes(flag)) {
		throw new ValidationError(
			`invalid sighash type: 0x${sigflag.toString(16)}. ` +
			`Valid values: SIGHASH_ALL (0x01), SIGHASH_NONE (0x02), SIGHASH_SINGLE (0x03), ` +
			`or combined with ANYONECANPAY (0x81, 0x82, 0x83)`
		);
	}
	// Unpack the tx object.
	const { version, vin, vout, locktime } = tx;
	// Parse the input we are signing from the config.
	const txinput = parse_txinput(tx, options);
	// Unpack the chosen input for signing.
	const { txid, vout: prevIdx, prevout, sequence } = txinput;
	// Unpack the prevout for the chosen input.
	const { value } = prevout ?? {};
	// Check if a prevout value is provided.
	if (value === undefined) {
		throw new ValidationError("Prevout value is required for segwit sighash calculation", "prevout.value");
	}
	// Initialize our script variable from the config.
	let { pubkey, script } = options;
	// Check if a pubkey is provided (instead of a script).
	if (script === undefined && pubkey !== undefined) {
		const pkhash = hash160(pubkey).hex;
		script = `76a914${String(pkhash)}88ac`;
	}
	// Make sure that some form of script has been provided.
	if (script === undefined) {
		throw new ValidationError("Either pubkey or script must be provided for segwit sighash", "pubkey/script");
	}
	// Throw if OP_CODESEPARATOR is used in a script.
	if (decode_script(script).includes("OP_CODESEPARATOR")) {
		throw new ValidationError(
			"OP_CODESEPARATOR is not supported in segwit scripts",
			"script",
		);
	}

	const sighash = [
		encode_tx_version(version),
		bip143_hash_prevouts(vin, is_anypay),
		bip143_hash_sequence(vin, flag, is_anypay),
		encode_txin_txid(txid),
		encode_txin_vout(prevIdx),
		prefix_script_size(script),
		encode_vout_value(value),
		encode_txin_sequence(sequence),
		bip143_hash_outputs(vout, flag, txindex),
		encode_tx_locktime(locktime),
		Buff.num(sigflag, 4).reverse(),
	];

	return hash256(Buff.join(sighash));
}

/**
 * Compute double-SHA256 hash of all input outpoints (txid:vout pairs).
 *
 * Returns zeroes if ANYONECANPAY flag is set.
 *
 * @param vin - Array of transaction inputs
 * @param isAnypay - Whether ANYONECANPAY flag is set
 * @returns Hash of outpoints, or 32 zero bytes if ANYONECANPAY
 */
export function bip143_hash_prevouts(
	vin: TxInput[],
	isAnypay?: boolean,
): Uint8Array {
	if (isAnypay === true) {
		return Buff.num(0, 32);
	}

	const stack = [];

	for (const { txid, vout } of vin) {
		stack.push(encode_txin_txid(txid));
		stack.push(encode_txin_vout(vout));
	}

	return hash256(Buff.join(stack));
}

/**
 * Compute double-SHA256 hash of all input sequence numbers.
 *
 * Returns zeroes if ANYONECANPAY flag is set or sigflag is not SIGHASH_ALL.
 *
 * @param vin - Array of transaction inputs
 * @param sigflag - Signature hash type (normalized, without ANYONECANPAY bit)
 * @param isAnyPay - Whether ANYONECANPAY flag is set
 * @returns Hash of sequences, or 32 zero bytes if conditions not met
 */
export function bip143_hash_sequence(
	vin: TxInput[],
	sigflag: number,
	isAnyPay: boolean,
): Uint8Array {
	if (isAnyPay || sigflag !== 0x01) {
		return Buff.num(0, 32);
	}

	const stack = [];

	for (const { sequence } of vin) {
		stack.push(encode_txin_sequence(sequence));
	}
	return hash256(Buff.join(stack));
}

/**
 * Compute double-SHA256 hash of transaction outputs.
 *
 * Behavior depends on sigflag:
 * - SIGHASH_ALL (0x01): Hash all outputs
 * - SIGHASH_SINGLE (0x03): Hash only the output at the given index
 * - SIGHASH_NONE (0x02): Return 32 zero bytes
 *
 * @param vout - Array of transaction outputs
 * @param sigflag - Signature hash type (normalized, without ANYONECANPAY bit)
 * @param idx - Input index (required for SIGHASH_SINGLE)
 * @returns Hash of outputs, or 32 zero bytes for SIGHASH_NONE
 */
export function bip143_hash_outputs(
	vout: TxOutput[],
	sigflag: number,
	idx?: number,
): Uint8Array {
	const stack = [];

	if (sigflag === 0x01) {
		for (const { value, script_pk } of vout) {
			stack.push(encode_vout_value(value));
			stack.push(prefix_script_size(script_pk));
		}
		return hash256(Buff.join(stack));
	}

	if (sigflag === 0x03) {
		Assert.ok(idx !== undefined, "txindex required for SIGHASH_SINGLE");
		Assert.ok(idx >= 0, "txindex must be non-negative");
		if (idx < vout.length) {
			const { value, script_pk } = vout[idx];
			stack.push(encode_vout_value(value));
			stack.push(prefix_script_size(script_pk));
			return hash256(Buff.join(stack));
		}
	}

	return Buff.num(0, 32);
}
